import { MetricsDictionary } from '@tw/types/module/metrics/metrics';
import { avg, divide, sum } from './utils';

export const shopMetrics: MetricsDictionary = {
  orders: {
    key: 'orders',
    label: 'Orders',
    shortLabel: 'Orders',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    chart: 'ordersChart',
  },
  sales: {
    key: 'sales',
    label: 'Sales',
    shortLabel: 'Sales',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    chart: 'salesChart',
  },
  refunds: {
    key: 'refunds',
    label: 'Refunds',
    shortLabel: 'Refunds',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    chart: 'chartRefunds',
    hideInActivities: true,
  },
  netSales: {
    key: 'netSales',
    label: 'Net Sales',
    shortLabel: 'Net Sales',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    chart: 'generalNetSalesChart',
  },
  itemsSold: {
    key: 'itemsSold',
    label: 'Items Sold',
    shortLabel: 'Items Sold',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    hideInActivities: true,
  },
  revenue: {
    key: 'revenue',
    label: 'Revenue',
    shortLabel: 'Revenue',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    calculateSum: (items) => {
      return sum(items, 'revenue');
    },
    hideInActivities: true,
  },
  ncRevenue: {
    key: 'ncRevenue',
    label: 'New Customer Revenue',
    shortLabel: 'New Customer Revenue',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify'],
    calculateSum: (items) => {
      return sum(items, 'ncRevenue');
    },
    chart: 'chartNewCustomerSales',
    hideInActivities: true,
  },
  ncOrdersWithProduct: {
    key: 'ncOrdersWithProduct',
    label: 'New Customer Orders',
    shortLabel: 'New Customer Orders',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: false,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify'],
    calculateSum: (items) => {
      return sum(items, 'ncOrdersWithProduct');
    },
    hideInActivities: true,
  },
  ncGrossProfit: {
    key: 'ncGrossProfit',
    label: 'New Customer Gross Profit',
    shortLabel: 'New Customer Gross Profit',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify'],
    calculateSum: (items) => {
      return sum(items, 'ncGrossProfit');
    },
    hideInActivities: true,
  },
  ordersWithProduct: {
    key: 'ordersWithProduct',
    label: 'Orders',
    shortLabel: 'Orders',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    hideInActivities: true,
    calculateSum: (items) => {
      return sum(items, 'ordersWithProduct');
    },
  },
  itemsSoldAvg: {
    key: 'itemsSoldAvg',
    label: 'Avg Sold',
    shortLabel: 'Avg Sold',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'itemsSoldAvg');
    },
    hideInActivities: true,
  },
  itemsSoldTotal: {
    key: 'itemsSoldTotal',
    label: 'Total Sold',
    shortLabel: 'Total Sold',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 0,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'itemsSoldTotal');
    },
    hideInActivities: true,
  },
  returningCustomerOrders: {
    key: 'returningCustomerOrders',
    label: 'Returning Customer Orders',
    shortLabel: 'Returning Customer Orders',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    hideInActivities: true,
  },
  returningCustomerSales: {
    key: 'returningCustomerSales',
    label: 'Returning Customer Sales',
    shortLabel: 'Returning Customer Sales',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    chart: 'chartReturningCustomerSales',
  },
  taxes: {
    key: 'taxes',
    label: 'Taxes',
    shortLabel: 'Taxes',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    hideInActivities: true,
  },
  shipping: {
    key: 'shipping',
    label: 'Shipping',
    shortLabel: 'Shipping',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    hideInActivities: true,
  },
  discounts: {
    key: 'discounts',
    label: 'Discounts',
    shortLabel: 'Discounts',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify', 'amazon'],
    hideInActivities: true,
  },
  newCustomerOrders: {
    key: 'newCustomerOrders',
    label: 'New Customer Orders',
    shortLabel: 'New Customer Orders',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    chart: 'chartNewCustomersOrders',
  },
  newCustomerSales: {
    key: 'newCustomerSales',
    label: 'New Customer Sales',
    shortLabel: 'New Customer Sales',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    chart: 'chartNewCustomerSales',
  },
  refundsRate: {
    key: 'refundsRate',
    label: 'Refunds Rate',
    shortLabel: 'Refunds Rate',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'percent',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: ['shopify'],
    hideInActivities: true,
  },
  itemsInInventory: {
    key: 'itemsInInventory',
    label: 'Inventory',
    shortLabel: 'Items in Inventory',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 0,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'itemsInInventory');
    },
    hideInActivities: true,
  },
  grossProfit: {
    key: 'grossProfit',
    label: 'Gross Profit',
    shortLabel: 'Gross Profit',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'grossProfit');
    },
    chart: 'grossProfitChart',
  },
  returnRate: {
    key: 'returnRate',
    label: 'Return Rate',
    shortLabel: 'Return Rate',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'percent',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    valueIsNegative: true,
    calculateSum: (items) => {
      return divide(items, 'returnsTotal', 'itemsSoldTotal');
    },
    hideInActivities: true,
  },
  returnsTotal: {
    key: 'returnsTotal',
    label: 'Returns',
    shortLabel: 'Returns',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'decimal',
    toFixed: 0,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    valueIsNegative: true,
    calculateSum: (items) => {
      return sum(items, 'returnsTotal');
    },
  },
  customerCount: {
    key: 'customerCount',
    label: 'Customers Count',
    shortLabel: 'Customers',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'decimal',
    toFixed: 0,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'customerCount');
    },
  },
  rpr: {
    key: 'rpr',
    label: 'RPR',
    shortLabel: 'Repeat Purchase Rate',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'percent',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'ordersWithProduct', 'customerCount');
    },
    hideInActivities: true,
  },
  productAov: {
    key: 'productAov',
    label: 'Product Average Order Value',
    shortLabel: 'AOV',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'orderValue', 'ordersWithProduct');
    },
  },
  productNcAov: {
    key: 'productNcAov',
    label: 'New Customer Average Order Value',
    shortLabel: 'NC AOV',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'ncOrderValue', 'ncOrdersWithProduct');
    },
  },
  orderValue: {
    key: 'orderValue',
    label: 'Total Order Value',
    shortLabel: 'TOV',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'orderValue');
    },
  },
  ncOrderValue: {
    key: 'ncOrderValue',
    label: 'New Customer Order Value',
    shortLabel: 'NC TOV',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'ncOrderValue');
    },
  },
  productCpa: {
    key: 'productCpa',
    label: 'CPA',
    shortLabel: 'CPA',
    hideInPixel: true,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    format: 'currency',
    toFixed: 2,
    type: 'ads',
    showInCreativeByDefault: true,
    showInPixelByDefault: false,
    allowOrderBy: true,
    valueIsNegative: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'ordersWithProduct');
    },
  },
  daysOfStock: {
    key: 'daysOfStock',
    label: 'Days of Stock',
    shortLabel: 'Days of Stock',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'itemsInInventory', 'avgSold30');
    },
    hideInActivities: true,
  },
  totalSold30: {
    key: 'totalSold30',
    label: 'Total Sold 30',
    shortLabel: 'Total 30',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 0,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'totalSold30');
    },
    hideInActivities: true,
  },
  avgSold30: {
    key: 'avgSold30',
    label: 'Avg Sold 30',
    shortLabel: 'Avg 30',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'decimal',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'totalSold30', 30);
    },
    hideInActivities: true,
  },
  ltv60: {
    key: 'ltv60',
    label: 'LTV 60',
    shortLabel: 'LTV 60',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'ltv60');
    },
    hideInActivities: true,
  },
  ltv90: {
    key: 'ltv90',
    label: 'LTV 90',
    shortLabel: 'LTV 90',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'ltv90');
    },
    hideInActivities: true,
  },
  ltv180: {
    key: 'ltv180',
    label: 'LTV 180',
    shortLabel: 'LTV 180',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'ltv180');
    },
    hideInActivities: true,
  },
  ltv365: {
    key: 'ltv365',
    label: 'LTV 365',
    shortLabel: 'LTV 365',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'shop',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'ltv365');
    },
    hideInActivities: true,
  },
  dateStockRunsOut: {
    key: 'dateStockRunsOut',
    label: 'Out of Stock Date',
    shortLabel: 'Out of Stock Date',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'string',
    type: 'shop',
    toFixed: 2,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    hideInActivities: true,
  },
  contributionMargin: {
    key: 'contributionMargin',
    label: 'Contribution Margin',
    shortLabel: 'CM',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'ads',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return sum(items, 'contributionMargin');
    },
    hideInActivities: true,
  },
  contributionMarginPerUnit: {
    key: 'contributionMarginPerUnit',
    label: 'CM per Unit',
    shortLabel: 'CM per Unit',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    format: 'currency',
    toFixed: 2,
    type: 'ads',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: false,
    hideInPixel: true,
    calculateSum: (items) => {
      return divide(items, 'contributionMargin', 'itemsSoldTotal');
    },
    hideInActivities: true,
  },
  adsInventory: {
    key: 'adsInventory',
    label: 'Inventory',
    shortLabel: 'Inventory',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInServices: [
      'facebook-ads',
      'google-ads',
      'tiktok-ads',
      'pinterest-ads',
      'snapchat-ads',
      'twitter-ads',
    ],
    format: 'string',
    toFixed: 2,
    type: 'ads',
    showInCreativeByDefault: false,
    showInPixelByDefault: true,
    allowOrderBy: false,
    hideInActivities: true,
  },
  avgPurchasePrice: {
    key: 'avgPurchasePrice',
    label: 'Average Purchase Price',
    shortLabel: 'Average Purchase Price',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    calculateSum: (items) => {
      return avg(items, 'avgPurchasePrice');
    },
    hideInActivities: true,
  },
  modePurchasePrice: {
    key: 'modePurchasePrice',
    label: 'Mode Purchase Price',
    shortLabel: 'Mode Purchase Price',
    hideInPixel: true,
    type: 'shop',
    toFixed: 2,
    format: 'currency',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    calculateSum: (items) => {
      return avg(items, 'modePurchasePrice');
    },
    hideInActivities: true,
  },
  addedToCartEvents: {
    key: 'addedToCartEvents',
    label: 'Added to cart',
    shortLabel: 'Added to cart',
    hideInPixel: true,
    type: 'shop',
    toFixed: 0,
    format: 'decimal',
    allowOrderBy: true,
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInServices: ['shopify', 'amazon'],
    calculateSum: (items) => {
      return sum(items, 'addedToCartEvents');
    },
    hideInActivities: true,
  },
};
